import {entries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {colNumToExcelId} from 'bulk-import/SpreadsheetBulkImportUtil';
import {TagSchema} from 'constants/RegistryConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {
  concentrationUnitTypePredicate,
  containerQuantityUnitTypePredicate,
} from 'inventory/InventoryUnitUtil';
import {
  AMOUNT_COLUMN_TYPES,
  CHEMICAL_STRUCTURE_COLUMN_TYPES,
  CONCENTRATION_COLUMN_TYPES,
  DATASET_COLUMN_TYPES,
  DESTINATION_COLUMN_TYPES,
  ENTITY_COLUMN_TYPES,
  IGNORE_COLUMN_TYPES,
  includeColumnTypeOption,
  PROCEDURE_CONDITION_REPLICATES_COLUMN_TYPES,
  RESIDUES_COLUMN_TYPES,
  SCHEMA_FIELD_COLUMN_TYPES,
  SOURCE_COLUMN_TYPES,
  VOLUME_COLUMN_TYPES,
} from 'lab-automation/settings/automation-output-file-config/AutomationOutputFileColumnUtils';
import {canSchemaHaveMods} from 'molbio/OligoUtil';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {StorableSchema, ContainerSchema} from 'samples/storables/StorableSchemaConstants';
import isSchemaFieldArchived from 'schemas/fields/isSchemaFieldArchived';
import {Schema} from 'schemas/SchemaConstants';
import {FONT_PROXIMA_13, FONT_PROXIMA_19, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Checkbox from 'ui-library/Checkbox';
import {GRAY_LIGHT} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import {LabelStyle, Size} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
import {UNIT_TYPES_QUERY} from 'units/UnitsConstants';
import sortedBy from 'util/sortedBy';

import {AddComponentButton, RemoveComponentButton} from '../RunSchemaEditorActionButton';
import {
  AutomationOutputFileColumnType,
  AutomationOutputFileColumnTypeMetadata,
  AutomationOutputFileConfigSchemaInfoType,
  AutomationOutputFileDestinationInfo,
  AutomationOutputFileEntitySchemaInfo,
  AutomationOutputFileResultSchemaInfo,
  getColumnTypeDisplayName,
  OutputFileAction,
  SchemaInfoFields,
  SetColumnsMappingType,
  TransformationFieldType,
} from '../RunSchemaSpecConstants';
import {getSchemaInfoFieldKey, getTransformationFieldInfo} from './SchemaInfoFieldKeys';

// exported for test
export const DELETE_DATA_TEST_ATTRS = {
  delete: {component: 'SetColumnsMapFieldsSection', element: 'delete'},
};
export const EDIT_DATA_TEST_ATTRS = {
  edit: {component: 'SetColumnsMapFieldsSection', element: 'edit'},
};
export const ADD_DATA_TEST_ATTRS = {
  add: {component: 'SetColumnsMapFieldsSection', element: 'add'},
};

export enum ColumnTypeHeaders {
  AMOUNT = 'AMOUNT',
  SOURCE = 'SOURCE',
  DESTINATION = 'DESTINATION',
  ENTITY = 'ENTITY',
  SCHEMA_FIELD = 'SCHEMA_FIELD',
  RESIDUES = 'RESIDUES',
  HELM = 'HELM',
  CUSTOM_NOTATION = 'CUSTOM_NOTATION',
  MOLFILE = 'MOLFILE',
  SMILES = 'SMILES',
  DATASET = 'DATASET', // DEPRECATED
  PROCEDURE_CONDITION_REPLICATES = 'PROCEDURE_CONDITION_REPLICATES',
  IGNORE = 'IGNORE',
}

export const columnTypeHeadersOptions: {[type in ColumnTypeHeaders]: string} = {
  AMOUNT: 'Amount',
  SOURCE: 'Source',
  DESTINATION: 'Destination',
  ENTITY: 'Entity',
  SCHEMA_FIELD: 'Schema Field',
  RESIDUES: 'Residues',
  HELM: 'HELM',
  CUSTOM_NOTATION: 'Custom notation',
  MOLFILE: 'Molfile',
  SMILES: 'SMILES',
  DATASET: 'Dataset', // DEPRECATED
  PROCEDURE_CONDITION_REPLICATES: 'Condition - Replicate',
  IGNORE: 'Ignore',
};

const columnTypeHeadersToColumnTypes: Map<ColumnTypeHeaders, Set<AutomationOutputFileColumnType>> = new Map([
  [ColumnTypeHeaders.AMOUNT, AMOUNT_COLUMN_TYPES],
  [ColumnTypeHeaders.SOURCE, SOURCE_COLUMN_TYPES],
  [ColumnTypeHeaders.DESTINATION, DESTINATION_COLUMN_TYPES],
  [ColumnTypeHeaders.ENTITY, ENTITY_COLUMN_TYPES],
  [ColumnTypeHeaders.SCHEMA_FIELD, SCHEMA_FIELD_COLUMN_TYPES],
  [ColumnTypeHeaders.RESIDUES, RESIDUES_COLUMN_TYPES],
  [ColumnTypeHeaders.HELM, RESIDUES_COLUMN_TYPES],
  [ColumnTypeHeaders.CUSTOM_NOTATION, RESIDUES_COLUMN_TYPES],
  [ColumnTypeHeaders.MOLFILE, CHEMICAL_STRUCTURE_COLUMN_TYPES],
  [ColumnTypeHeaders.SMILES, CHEMICAL_STRUCTURE_COLUMN_TYPES],
  [ColumnTypeHeaders.DATASET, DATASET_COLUMN_TYPES], // DEPRECATED
  [ColumnTypeHeaders.PROCEDURE_CONDITION_REPLICATES, PROCEDURE_CONDITION_REPLICATES_COLUMN_TYPES],
  [ColumnTypeHeaders.IGNORE, IGNORE_COLUMN_TYPES],
]);

const unitSymbolToOptionData = (
  symbol: string,
  columnName: string,
  columnType: AutomationOutputFileColumnType
): OptionData => ({
  name: symbol,
  value: {
    name: columnName,
    columnType,
    unit: symbol,
    isRequired: true,
  },
});

function getUnitOptionsForColumnType(
  columnType: AutomationOutputFileColumnType,
  columnTypeMetadata: AutomationOutputFileColumnTypeMetadata,
  allUnitTypes: UnitTypesQuery['unitTypes']
): Array<OptionData> {
  switch (columnType) {
    case AutomationOutputFileColumnType.FINAL_QUANTITY_AMOUNT:
    case AutomationOutputFileColumnType.SOURCE_FINAL_QUANTITY_AMOUNT:
    case AutomationOutputFileColumnType.TRANSFER_QUANTITY_AMOUNT:
      return allUnitTypes
        .filter((ut) => containerQuantityUnitTypePredicate(ut))
        .flatMap((ut) => [
          {name: ut.name, isHeader: true},
          ...ut.units.map((u) => unitSymbolToOptionData(u.symbol, columnTypeMetadata.name!, columnType)),
        ]);
    case AutomationOutputFileColumnType.FINAL_CONCENTRATION_VALUE:
    case AutomationOutputFileColumnType.INPUT_CONCENTRATION_VALUE:
      return allUnitTypes
        .filter((ut) => concentrationUnitTypePredicate(ut))
        .flatMap((ut) => [
          {name: ut.name, isHeader: true},
          ...ut.units.map((u) => unitSymbolToOptionData(u.symbol, columnTypeMetadata.name!, columnType)),
        ]);
    default:
      return [];
  }
}

export interface SetColumnsMapFieldsSectionProps {
  columnTypeMetadatas: Array<AutomationOutputFileColumnTypeMetadata>;
  setColumnsMappingType: SetColumnsMappingType;
  volumeUnits: string;
  concentrationUnits: string;
  destinationInfo: AutomationOutputFileDestinationInfo | undefined | null;
  destinationSchema: StorableSchema | null;
  wellContainerSchema: ContainerSchema | null;
  /** A plate's or box's container schema, whose fields a destination without a plate transfer can also set. */
  gridContainerSchema?: ContainerSchema | null;
  entitySchemaInfo: AutomationOutputFileEntitySchemaInfo | undefined | null;
  entitySchema: TagSchema | null;
  resultSchemaInfo: AutomationOutputFileResultSchemaInfo | undefined | null;
  resultSchema: ResultSchema | null;
  actionType: OutputFileAction;
  shouldPerformPlateTransfer: boolean;
  onAddColumnTypeMetadata: (columnTypeMetadata: AutomationOutputFileColumnTypeMetadata) => void;
  onUpdateColumnTypeMetadata: (
    index: number,
    updatedColumnTypeMetadata: AutomationOutputFileColumnTypeMetadata
  ) => void;
  onUpdateColumnTypeMetadatas: (
    updatedColumnTypeMetadatas: Array<AutomationOutputFileColumnTypeMetadata>
  ) => void;
  onRemoveColumnTypeMetadata: (index: number) => void;
  onUpdateAmount: (columnType: AutomationOutputFileColumnType, unit: string) => void;
}

function getTextFieldByColumnName(
  props: SetColumnsMapFieldsSectionProps,
  index: number,
  columnTypeMetadata: AutomationOutputFileColumnTypeMetadata
): JSX.Element {
  return (
    <TextField
      label={`Column name ${index}`}
      labelStyle={LabelStyle.HIDDEN}
      value={columnTypeMetadata.name ?? ''}
      size={Size.MEDIUM}
      onChange={(e) => {
        props.onUpdateColumnTypeMetadata(index, {
          ...columnTypeMetadata,
          name: e.target.value,
        });
      }}
      placeholder="Column name"
    />
  );
}

function getDisplayName(
  columnTypeMetadata: AutomationOutputFileColumnTypeMetadata,
  volumeUnits: string,
  concentrationUnits: string
): string {
  if (!columnTypeMetadata.columnType) {
    return 'Choose column type...';
  }
  if (CONCENTRATION_COLUMN_TYPES.has(columnTypeMetadata.columnType)) {
    return `${getColumnTypeDisplayName(columnTypeMetadata.columnType)} (${concentrationUnits})`;
  }
  if (VOLUME_COLUMN_TYPES.has(columnTypeMetadata.columnType)) {
    return `${getColumnTypeDisplayName(columnTypeMetadata.columnType)} (${volumeUnits})`;
  }
  if (SCHEMA_FIELD_COLUMN_TYPES.has(columnTypeMetadata.columnType) && columnTypeMetadata.schemaField) {
    return `${getColumnTypeDisplayName(columnTypeMetadata.columnType)}: ${columnTypeMetadata.schemaField}`;
  }
  if (
    columnTypeMetadata.columnType === AutomationOutputFileColumnType.CUSTOM_NOTATION &&
    'customSyntaxId' in columnTypeMetadata
  ) {
    const customSyntaxName =
      BenchlingConfig.MODBIO_CUSTOM_SYNTAX_NAMES_BY_ID[columnTypeMetadata.customSyntaxId!];
    return `${getColumnTypeDisplayName(columnTypeMetadata.columnType)}: ${customSyntaxName || columnTypeMetadata.customSyntaxId}`;
  }
  return getColumnTypeDisplayName(columnTypeMetadata.columnType);
}

// export for test
export function _getDestinationInfoSchemaId(destinationInfo: AutomationOutputFileDestinationInfo): string {
  if (destinationInfo.boxSchema) {
    return destinationInfo.boxSchema;
  }
  if (destinationInfo.containerSchema) {
    return destinationInfo.containerSchema;
  }
  return destinationInfo.plateSchema!;
}

interface SchemaFieldOption {
  fieldName: string;
  /** The field's key in its schema info's `fields` (see getSchemaInfoFieldKey), if its schema exists. */
  fieldKey?: string;
  schemaId: string | null;
  schemaName: string | null;
  schemaInfoType: AutomationOutputFileConfigSchemaInfoType;
  isRequired: boolean;
}

// export for test
export function _getAvailableSchemaFields(
  destinationInfo: AutomationOutputFileDestinationInfo | undefined | null,
  entitySchemaInfo: AutomationOutputFileEntitySchemaInfo | undefined | null,
  resultSchemaInfo: AutomationOutputFileResultSchemaInfo | undefined | null,
  destinationSchema: StorableSchema | null,
  wellContainerSchema: ContainerSchema | null,
  entitySchema: TagSchema | null,
  resultSchema: ResultSchema | null,
  shouldPerformPlateTransfer: boolean | null,
  gridContainerSchema: ContainerSchema | null = null
): Array<SchemaFieldOption> {
  let availableSchemaFields: Array<SchemaFieldOption> = [];
  if (destinationInfo) {
    if (shouldPerformPlateTransfer) {
      availableSchemaFields = [
        ...availableSchemaFields,
        ..._getAvailableSchemaFieldsHelper(
          wellContainerSchema ?? null,
          destinationInfo.fields,
          AutomationOutputFileConfigSchemaInfoType.DESTINATION_INFO
        ),
      ];
    } else {
      availableSchemaFields = [
        ...availableSchemaFields,
        ..._getAvailableSchemaFieldsHelper(
          destinationSchema ?? null,
          destinationInfo.fields,
          AutomationOutputFileConfigSchemaInfoType.DESTINATION_INFO
        ),
        // Without a plate transfer, a plate's or box's output file can also set fields on its wells or containers.
        ..._getAvailableSchemaFieldsHelper(
          gridContainerSchema,
          destinationInfo.fields,
          AutomationOutputFileConfigSchemaInfoType.DESTINATION_INFO
        ),
      ];
    }
  }
  if (entitySchemaInfo) {
    availableSchemaFields = [
      ...availableSchemaFields,
      ..._getAvailableSchemaFieldsHelper(
        entitySchema ?? null,
        entitySchemaInfo.fields,
        AutomationOutputFileConfigSchemaInfoType.ENTITY_SCHEMA_INFO
      ),
    ];
  }
  if (resultSchemaInfo) {
    availableSchemaFields = [
      ...availableSchemaFields,
      ..._getAvailableSchemaFieldsHelper(
        resultSchema ?? null,
        resultSchemaInfo.fields,
        AutomationOutputFileConfigSchemaInfoType.RESULT_SCHEMA_INFO
      ),
    ];
  }
  return availableSchemaFields;
}

export function getNewEntitySchemaField(): SchemaFieldOption {
  return {
    fieldName: 'New entity schema field',
    schemaId: null,
    schemaName: null,
    schemaInfoType: AutomationOutputFileConfigSchemaInfoType.ENTITY_SCHEMA_INFO,
    isRequired: false,
  };
}

export function getNewResultSchemaField(): SchemaFieldOption {
  return {
    fieldName: 'New result schema field',
    schemaId: null,
    schemaName: null,
    schemaInfoType: AutomationOutputFileConfigSchemaInfoType.RESULT_SCHEMA_INFO,
    isRequired: false,
  };
}

export function _getNewSchemaFields(
  destinationInfo: AutomationOutputFileDestinationInfo | undefined | null,
  entitySchemaInfo: AutomationOutputFileEntitySchemaInfo | undefined | null,
  resultSchemaInfo: AutomationOutputFileResultSchemaInfo | undefined | null
): Array<SchemaFieldOption> {
  const options: Array<SchemaFieldOption> = [];
  if (entitySchemaInfo && entitySchemaInfo.schema == null) {
    options.push(getNewEntitySchemaField());
  }
  if (resultSchemaInfo && resultSchemaInfo.schema == null) {
    options.push(getNewResultSchemaField());
  }
  return options;
}

function _getAvailableSchemaFieldsHelper(
  schema: Schema | null,
  fields: SchemaInfoFields,
  schemaInfoType: AutomationOutputFileConfigSchemaInfoType
): Array<SchemaFieldOption> {
  if (schema == null) {
    return [];
  }
  return schema.allFields
    .filter((field) => {
      const transformationFieldInfo = getTransformationFieldInfo(fields, field);
      if (transformationFieldInfo == null) {
        // Only include archived fields if they were previously set up via JSON
        return !isSchemaFieldArchived(field);
      }
      return transformationFieldInfo.type === TransformationFieldType.DIRECT;
    })
    .map((field) => ({
      fieldName: field.name,
      fieldKey: getSchemaInfoFieldKey(fields, field),
      schemaId: schema.id,
      schemaName: schema.name,
      schemaInfoType,
      isRequired: field.isRequired,
    }));
}

// export for test
export function _getSchemaFieldOptionName(schemaFieldOption: SchemaFieldOption): string {
  if (schemaFieldOption.schemaId) {
    const schemaName = schemaFieldOption.schemaName ?? 'Deleted Schema';
    return `${schemaName} field: ${schemaFieldOption.fieldName}`;
  }
  return schemaFieldOption.fieldName;
}

export function getColumnTypeMetadataFromSchemaFieldOption(
  schemaFieldOption: SchemaFieldOption,
  name: string | undefined,
  isRequired: boolean
): AutomationOutputFileColumnTypeMetadata {
  return {
    columnType: AutomationOutputFileColumnType.SCHEMA_FIELD,
    name,
    schemaField: schemaFieldOption.fieldName,
    schemaFieldKey: schemaFieldOption.fieldKey,
    schemaInfoType: schemaFieldOption.schemaInfoType,
    schemaId: schemaFieldOption.schemaId,
    isFieldRequired: schemaFieldOption.isRequired,
    isRequired,
  };
}

function _isRequiredDisabled(columnTypeMetadata: AutomationOutputFileColumnTypeMetadata): boolean {
  return columnTypeMetadata.columnType === AutomationOutputFileColumnType.SCHEMA_FIELD
    ? Boolean(columnTypeMetadata.isFieldRequired)
    : columnTypeMetadata.columnType !== AutomationOutputFileColumnType.IGNORE;
}

export default function SetColumnsMapFieldsSection(
  props: SetColumnsMapFieldsSectionProps
): JSX.Element | null {
  function _getColumnTypesOptionDatas(
    columnTypeMetadata: AutomationOutputFileColumnTypeMetadata,
    unitTypes: UnitTypesQuery['unitTypes']
  ): Array<OptionData> {
    const hasEntitySchema = props.entitySchema != null;
    const shouldShowResidues =
      hasEntitySchema && TagSchemaUtil.entityTypeSupportsResidues(props.entitySchema!.folderItemType);
    const shouldShowModifiedNucleotideColumns = hasEntitySchema && canSchemaHaveMods(props.entitySchema!);
    const shouldShowChemicalStructureColumns =
      BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE &&
      hasEntitySchema &&
      TagSchemaUtil.entityTypeSupportsChemicalStructure(props.entitySchema!.folderItemType);
    const shouldShowCustomNotationColumns =
      BenchlingConfig.MODBIO_ENABLE_CUSTOM_SYNTAXES &&
      shouldShowModifiedNucleotideColumns &&
      Object.keys(BenchlingConfig.MODBIO_CUSTOM_SYNTAX_NAMES_BY_ID).length > 0;

    const availableExistingSchemaFields = _getAvailableSchemaFields(
      props.destinationInfo,
      props.entitySchemaInfo,
      props.resultSchemaInfo,
      props.destinationSchema,
      props.wellContainerSchema,
      props.entitySchema,
      props.resultSchema,
      props.shouldPerformPlateTransfer,
      props.gridContainerSchema ?? null
    ).filter(
      (fieldOption) =>
        !props.columnTypeMetadatas.some(
          (mappedColumn) =>
            mappedColumn.schemaField === fieldOption.fieldName &&
            mappedColumn.schemaInfoType === fieldOption.schemaInfoType &&
            mappedColumn.schemaId === fieldOption.schemaId
        )
    );
    const availableSchemaFields = [
      ...availableExistingSchemaFields,
      ..._getNewSchemaFields(props.destinationInfo, props.entitySchemaInfo, props.resultSchemaInfo),
    ];
    const optionDatas: Array<OptionData> = [];
    for (const value of Object.keys(ColumnTypeHeaders) as Array<ColumnTypeHeaders>) {
      if (value === ColumnTypeHeaders.IGNORE) {
        optionDatas.push({
          name: columnTypeHeadersOptions[value],
          value: {
            name: columnTypeMetadata.name,
            columnType: [...columnTypeHeadersToColumnTypes.get(value)!][0],
            isRequired: columnTypeMetadata.isRequired,
          },
        });
      } else if (value === ColumnTypeHeaders.RESIDUES && shouldShowResidues) {
        optionDatas.push({
          name: columnTypeHeadersOptions[value],
          value: {
            name: columnTypeMetadata.name,
            columnType: AutomationOutputFileColumnType.RESIDUES,
            isRequired: true,
          },
        });
      } else if (
        value === ColumnTypeHeaders.PROCEDURE_CONDITION_REPLICATES &&
        BenchlingConfig.PROCEDURES_ENABLE
      ) {
        optionDatas.push({
          name: columnTypeHeadersOptions[value],
          value: {
            name: columnTypeMetadata.name,
            columnType: AutomationOutputFileColumnType.PROCEDURE_CONDITION_REPLICATES,
            isRequired: columnTypeMetadata.isRequired,
          },
        });
      } else if (value === ColumnTypeHeaders.HELM && shouldShowModifiedNucleotideColumns) {
        optionDatas.push({
          name: columnTypeHeadersOptions[value],
          value: {
            name: columnTypeMetadata.name,
            columnType: AutomationOutputFileColumnType.HELM,
            isRequired: true,
          },
        });
      } else if (value === ColumnTypeHeaders.CUSTOM_NOTATION && shouldShowCustomNotationColumns) {
        const sortedCustomSyntaxEntries = sortedBy(
          [...entries(BenchlingConfig.MODBIO_CUSTOM_SYNTAX_NAMES_BY_ID)],
          [{accessor: ([, syntaxName]) => syntaxName, naturalSort: true}]
        );
        optionDatas.push({
          name: columnTypeHeadersOptions[value],
          submenu: [
            sortedCustomSyntaxEntries.map(([customSyntaxId, customSyntaxName]) => ({
              name: customSyntaxName,
              value: {
                name: columnTypeMetadata.name,
                columnType: AutomationOutputFileColumnType.CUSTOM_NOTATION,
                isRequired: true,
                customSyntaxId,
              },
            })),
          ],
        });
      } else if (value === ColumnTypeHeaders.MOLFILE && shouldShowChemicalStructureColumns) {
        optionDatas.push({
          name: columnTypeHeadersOptions[value],
          value: {
            name: columnTypeMetadata.name,
            columnType: AutomationOutputFileColumnType.MOLFILE,
            isRequired: true,
          },
        });
      } else if (value === ColumnTypeHeaders.SMILES && shouldShowChemicalStructureColumns) {
        optionDatas.push({
          name: columnTypeHeadersOptions[value],
          value: {
            name: columnTypeMetadata.name,
            columnType: AutomationOutputFileColumnType.SMILES,
            isRequired: true,
          },
        });
      } else if (
        [ColumnTypeHeaders.SOURCE, ColumnTypeHeaders.DESTINATION, ColumnTypeHeaders.ENTITY].includes(value)
      ) {
        const availableOptions = [...columnTypeHeadersToColumnTypes.get(value)!].filter((columnType) =>
          includeColumnTypeOption(
            columnType,
            props.actionType,
            props.destinationInfo,
            props.entitySchema,
            false,
            props.shouldPerformPlateTransfer
          )
        );
        if (availableOptions.length !== 0) {
          optionDatas.push({
            name: columnTypeHeadersOptions[value],
            submenu: [
              availableOptions.map((columnType): OptionData => ({
                name: getColumnTypeDisplayName(columnType),
                value: {
                  name: columnTypeMetadata.name,
                  columnType,
                  isRequired: true,
                },
              })),
            ],
          });
        }
      } else if (value === ColumnTypeHeaders.SCHEMA_FIELD) {
        if (availableSchemaFields.length !== 0) {
          optionDatas.push({
            name: columnTypeHeadersOptions[value],
            submenu: [
              availableSchemaFields.map((availableSchemaField): OptionData => ({
                name: _getSchemaFieldOptionName(availableSchemaField),
                value: getColumnTypeMetadataFromSchemaFieldOption(
                  availableSchemaField,
                  columnTypeMetadata.name,
                  availableSchemaField.isRequired ? true : (columnTypeMetadata.isRequired ?? true)
                ),
              })),
            ],
          });
        }
      } else if (value === ColumnTypeHeaders.AMOUNT) {
        const availableOptions = [...columnTypeHeadersToColumnTypes.get(value)!].filter((columnType) =>
          includeColumnTypeOption(
            columnType,
            props.actionType,
            props.destinationInfo,
            props.entitySchema,
            false,
            props.shouldPerformPlateTransfer
          )
        );
        if (availableOptions.length !== 0) {
          optionDatas.push({
            name: columnTypeHeadersOptions[value],
            submenu: [
              availableOptions.map((columnType): OptionData => ({
                name: getColumnTypeDisplayName(columnType),
                submenu: [getUnitOptionsForColumnType(columnType, columnTypeMetadata, unitTypes)],
              })),
            ],
          });
        }
      }
    }
    return optionDatas;
  }

  function _filterArchivedUnitTypes(unitTypes: UnitTypesQuery['unitTypes']): UnitTypesQuery['unitTypes'] {
    return unitTypes.filter((unitType) => !unitType.archiveData?.archived);
  }

  return (
    <BenchlingQuery<UnitTypesQuery>
      query={UNIT_TYPES_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
    >
      {({unitTypes}) => (
        <>
          <Row className={css(styles.headerRow)}>
            <Col md={5} className={css(styles.headerColumn)}>
              Column
            </Col>
            <Col md={4} className={css(styles.headerColumn)}>
              Column type
            </Col>
            <Col md={3} className={css(styles.headerColumn)}>
              {props.setColumnsMappingType === SetColumnsMappingType.BY_COLUMN_NAME ? (
                <label>
                  <IndeterminateCheckbox
                    disabled={props.columnTypeMetadatas.length === 0}
                    items={props.columnTypeMetadatas}
                    selectedMap={(columnTypeMetadata: AutomationOutputFileColumnTypeMetadata): boolean =>
                      columnTypeMetadata.isRequired ?? true
                    }
                    disabledMap={_isRequiredDisabled}
                    onChange={() => {
                      const anySelected = props.columnTypeMetadatas.some(
                        (columnTypeMetadata): boolean =>
                          !_isRequiredDisabled(columnTypeMetadata) && (columnTypeMetadata.isRequired ?? true)
                      );
                      props.onUpdateColumnTypeMetadatas(
                        props.columnTypeMetadatas.map(
                          (columnTypeMetadata): AutomationOutputFileColumnTypeMetadata =>
                            _isRequiredDisabled(columnTypeMetadata)
                              ? columnTypeMetadata
                              : {
                                  ...columnTypeMetadata,
                                  isRequired: !anySelected,
                                }
                        )
                      );
                    }}
                  />
                  <span>Required?</span>
                </label>
              ) : null}
            </Col>
          </Row>
          {props.columnTypeMetadatas.map((columnTypeMetadata, index) => (
            <Row key={index} className={css(styles.row)}>
              <Col md={5} className={css(styles.column)}>
                {props.setColumnsMappingType === SetColumnsMappingType.BY_COLUMN_NAME ? (
                  getTextFieldByColumnName(props, index, columnTypeMetadata)
                ) : (
                  <div>Column {colNumToExcelId(index)}</div>
                )}
              </Col>
              <Col md={4} className={css(styles.column)}>
                <SelectDropdown
                  displayName={getDisplayName(
                    columnTypeMetadata,
                    props.volumeUnits,
                    props.concentrationUnits
                  )}
                  dataTestAttrs={{
                    component: 'SetColumnsMapFieldsSection',
                    element: 'selectDropdown',
                  }}
                  menu={
                    <SearchableMenu
                      selectedValue={
                        columnTypeMetadata.columnType
                          ? getColumnTypeDisplayName(columnTypeMetadata.columnType)
                          : 'select'
                      }
                      onSelect={(value) => {
                        if (_isRequiredDisabled(value)) {
                          value = {...value, isRequired: true};
                        }
                        if (AMOUNT_COLUMN_TYPES.has(value.columnType)) {
                          const {unit, ...columnTypeMetadata} = value;
                          props.onUpdateColumnTypeMetadata(index, columnTypeMetadata);
                          props.onUpdateAmount(value.columnType, unit);
                        } else {
                          props.onUpdateColumnTypeMetadata(index, value);
                        }
                      }}
                      optionDatas={_getColumnTypesOptionDatas(
                        columnTypeMetadata,
                        _filterArchivedUnitTypes(unitTypes)
                      )}
                      height={Menu.HEIGHTS.MEDIUM}
                      width={Menu.WIDTHS.FULL}
                    />
                  }
                />
              </Col>
              <Col md={2} className={css(styles.column)}>
                {props.setColumnsMappingType === SetColumnsMappingType.BY_COLUMN_NAME ? (
                  <Checkbox
                    checked={columnTypeMetadata.isRequired ?? true}
                    disabled={_isRequiredDisabled(columnTypeMetadata)}
                    onChange={() => {
                      const current = columnTypeMetadata.isRequired ?? true;
                      props.onUpdateColumnTypeMetadata(index, {...columnTypeMetadata, isRequired: !current});
                    }}
                  />
                ) : null}
              </Col>
              <Col md={1} className={css(styles.column)}>
                <RemoveComponentButton
                  label="Remove"
                  onClick={() => {
                    props.onRemoveColumnTypeMetadata(index);
                  }}
                  dataTestAttrs={DELETE_DATA_TEST_ATTRS.delete}
                />
              </Col>
            </Row>
          ))}
          <Row className={css(styles.row)}>
            <Col md={6} className={css(styles.column)}>
              <AddComponentButton
                label="Add Column"
                onClick={() => {
                  props.onAddColumnTypeMetadata({});
                }}
                dataTestAttrs={ADD_DATA_TEST_ATTRS.add}
              />
              <a
                className={css(styles.headerColumn)}
                onClick={() => {
                  props.onAddColumnTypeMetadata({});
                }}
              >
                Add Column
              </a>
            </Col>
          </Row>
        </>
      )}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  row: {
    borderColor: GRAY_LIGHT,
    borderStyle: 'solid',
    borderWidth: 0,
    borderTopWidth: 1,
    ...FONT_PROXIMA_13,
    margin: 0,
  },
  headerRow: {
    borderColor: GRAY_LIGHT,
    borderStyle: 'solid',
    borderWidth: 0,
    borderBottomWidth: 1,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    ...FONT_PROXIMA_13,
    margin: 0,
  },
  plus: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    ...FONT_PROXIMA_19,
  },
  column: {
    padding: '13px',
  },
  headerColumn: {
    marginBottom: '8px',
  },
  itemButton: {
    flex: 0.1,
    marginRight: '-24px',
  },
});
